import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BACKEND_URL from "../config/config";
import {
  persistFeatureFlags,
  resolveFeatureFlags,
} from "../config/featureFlags";
import { showPopup } from "../utils/popup";

export default function useRegistrationForm(language = "ES") {
  const getStoredValue = (key, defaultValue = "") =>
    sessionStorage.getItem(key) || defaultValue;

  const setStoredValue = (key, value) => {
    sessionStorage.setItem(
      key,
      typeof value === "string" ? value : JSON.stringify(value)
    );
  };

  const [firstName, setFirstName] = useState(getStoredValue("first_name"));
  const [lastName, setLastName] = useState(getStoredValue("last_name"));
  const [dni, setDni] = useState(getStoredValue("dni"));

  const [email, setEmail] = useState(getStoredValue("email"));
  const [emailError, setEmailError] = useState("");
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);

  const [phone, setPhone] = useState(getStoredValue("phone"));
  const [position, setPosition] = useState(getStoredValue("position"));
  const [region, setRegion] = useState(getStoredValue("region"));
  const [sector, setSector] = useState(getStoredValue("sector"));
  const [employees, setEmployees] = useState(getStoredValue("employees"));
  const [invitationCode, setInvitationCode] = useState(
    getStoredValue("invitation_code")
  );

  const [formErrors, setFormErrors] = useState({});
  const [invitationError, setInvitationError] = useState("");
  const [invitationSuccess, setInvitationSuccess] = useState("");
  const [dniStatusError, setDniStatusError] = useState("");
  const [isValidatingDni, setIsValidatingDni] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [panelTopics, setPanelTopics] = useState(getStoredValue("panel_topics"));
  const [businessGroup, setBusinessGroup] = useState(
    getStoredValue("business_group", "")
  );
  const [mainRole, setMainRole] = useState(
    getStoredValue("main_role", "")
  );
  const [mainRoleOther, setMainRoleOther] = useState(
    getStoredValue("main_role_other", "")
  );

  const [mainInterest, setMainInterest] = useState(
    getStoredValue("main_interest", "")
  );
  const [mainInterestOther, setMainInterestOther] = useState(
    getStoredValue("main_interest_other", "")
  );
  const [eventSource, setEventSource] = useState(
    getStoredValue("event_source", "")
  );
  const [eventSourceOther, setEventSourceOther] = useState(
    getStoredValue("event_source_other", "")
  );

  const [publicConfig, setPublicConfig] = useState(null);
  const [publicConfigError, setPublicConfigError] = useState("");
  const [isLoadingPublicConfig, setIsLoadingPublicConfig] = useState(true);

  const currency = (publicConfig?.currency || "ARS").toUpperCase();
  const basePrice = Number(publicConfig?.price_amount ?? publicConfig?.price ?? 0);
  const {
    IS_FREE,
    ENABLE_EMISSION,
    ENABLE_QUESTIONS,
  } = resolveFeatureFlags(publicConfig || {});

  const USA_MODE = process.env.REACT_APP_COUNTRY === "US";

  const [fullName, setFullName] = useState(getStoredValue("full_name"));

  const navigate = useNavigate();

  const [company, setCompany] = useState(() => {
    const stored = sessionStorage.getItem("id_company");
    return stored ? parseInt(stored, 10) : "";
  });

  const [companyOptions, setCompanyOptions] = useState([]);

  const t = {
    EN: {
      configError:
        "Could not load code validation. Refresh the page and try again.",
      invalidFormat: "Invalid format.",
      invalidFormatHint: "Invalid format.",
      freeEntry: "Code applied: free ticket.",
      usageLimit: "This code has reached its usage limit.",
      codeNotFound: "Code not recognized.",
      verifyError: "Could not verify the code.",
      validatingError: "Error validating the code.",
      codeNotValid: "The code is not valid yet.",
    },
    ES: {
      configError:
        "No se pudo cargar la validación del código. Recargá la página e intentá nuevamente.",
      invalidFormat: "Formato inválido.",
      invalidFormatHint: "Formato inválido.",
      freeEntry: "Código aplicado: entrada gratuita.",
      usageLimit: "Este código alcanzó su límite de usos.",
      codeNotFound: "Código no reconocido.",
      verifyError: "No se pudo verificar el código.",
      validatingError: "Error corroborando el código.",
      codeNotValid: "El código todavía no es válido.",
    },
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoadingPublicConfig(true);
        setPublicConfigError("");

        const res = await fetch(`${BACKEND_URL}/public-config`);
        if (!res.ok) throw new Error("No se pudo cargar public-config");

        const data = await res.json();
        if (cancelled) return;

        persistFeatureFlags(data);
        setPublicConfig(data);
      } catch (e) {
        if (!cancelled) {
          setPublicConfig(null);
          setPublicConfigError(t[language].configError);
        }
      } finally {
        if (!cancelled) setIsLoadingPublicConfig(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const CODE_RE = useMemo(() => {
    if (!publicConfig?.invite_code_regex) return null;
    try {
      return new RegExp(publicConfig.invite_code_regex);
    } catch {
      return null;
    }
  }, [publicConfig]);

  const validarEmail = (value) => {
    const regex = /^[A-Za-z0-9._%+-]{2,}@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,}$/;
    return regex.test(value);
  };

  const BASE_PRICE = basePrice;

  const [codeValid, setCodeValid] = useState(
    sessionStorage.getItem("code_is_valid") === "true"
  );

  const [finalPrice, setFinalPrice] = useState(() => {
    const stored = sessionStorage.getItem("final_price");
    return stored ? Number(stored) : BASE_PRICE;
  });

  const resetForm = () => {
    sessionStorage.clear();

    localStorage.removeItem("code_is_valid");
    localStorage.removeItem("email");
    localStorage.removeItem("final_price");
    localStorage.removeItem("currency");
    localStorage.removeItem("user_id");

    setFirstName("");
    setLastName("");
    setFullName("");
    setDni("");
    setEmail("");
    setEmailError("");
    setPhone("");
    setPosition("");
    setRegion("");
    setSector("");
    setEmployees("");
    setInvitationCode("");
    setInvitationError("");
    setInvitationSuccess("");
    setDniStatusError("");
    setFormErrors({});
    setCompany("");
    setCompanyOptions([]);
    sessionStorage.removeItem("id_company");
    sessionStorage.removeItem("company_name");
    setCodeValid(false);
    setFinalPrice(BASE_PRICE);
    setBusinessGroup("");
    setMainRole("");
    setMainRoleOther("");
    setMainInterest("");
    setMainInterestOther("");
    setEventSource("");
    setEventSourceOther("");

    sessionStorage.removeItem("business_group");
    sessionStorage.removeItem("main_role");
    sessionStorage.removeItem("main_role_other");
    sessionStorage.removeItem("main_interest");
    sessionStorage.removeItem("main_interest_other");
    sessionStorage.removeItem("event_source");
    sessionStorage.removeItem("event_source_other");
  };

  useEffect(() => {
    const completed = localStorage.getItem("flow_completed") === "true";
    if (completed) {
      localStorage.removeItem("flow_completed");
      resetForm();
    }
  }, []);

  useEffect(() => {
    if (!publicConfig) return;
    if (!IS_FREE && !codeValid) {
      setFinalPrice(BASE_PRICE);
      sessionStorage.setItem("final_price", String(BASE_PRICE));
      sessionStorage.setItem("currency", currency);
    }
  }, [publicConfig, BASE_PRICE, currency, codeValid]);

  useEffect(() => {
    if (IS_FREE) {
      setFinalPrice(0);
      setCodeValid(false);
      sessionStorage.setItem("code_is_valid", "false");
      sessionStorage.setItem("final_price", "0");
      sessionStorage.setItem("currency", currency);
    }
  }, [currency]);

  useEffect(() => {
    const trimmedEmail = (email || "").trim();

    setEmailError("");

    if (!trimmedEmail || !validarEmail(trimmedEmail)) return;

    const timeoutId = setTimeout(async () => {
      try {
        setIsValidatingEmail(true);

        const res = await fetch(
          `${BACKEND_URL}/validate-email/${encodeURIComponent(trimmedEmail)}`
        );
        const data = await res.json();

        if (!data.valid) {
          const reason = data.reason;
          if (reason === "email_in_use_by_other_dni") {
            setEmailError("Este email ya está asociado a otro asistente.");
          } else if (reason === "approved") {
            setEmailError("Este email ya tiene una inscripción aprobada.");
          } else if (reason === "pending") {
            setEmailError("Este email tiene un pago pendiente.");
          } else if (reason === "in_process") {
            setEmailError("Este email tiene un pago en proceso.");
          } else if (reason === "invited_exists") {
            setEmailError("Este email ya fue confirmado con un código de invitación.");
          } else {
            setEmailError("Este email no puede usarse.");
          }
        }
      } catch {
        setEmailError("Error validando el email. Intentá nuevamente.");
      } finally {
        setIsValidatingEmail(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [email]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/companies`);
        const data = await res.json();
        const formatted = data.map((company) => ({
          label: company.company_name,
          value: company.id_company,
        }));
        setCompanyOptions(formatted);
      } catch (err) {
        console.error("Error al cargar empresas:", err);
      }
    };

    fetchCompanies();
  }, []);

  const handleCompanyChange = (selectedOption) => {
    if (!selectedOption) {
      setCompany("");
      sessionStorage.removeItem("id_company");
      sessionStorage.removeItem("company_name");
      return;
    }

    setCompany(selectedOption.value);
    setStoredValue("id_company", selectedOption.value);
    setStoredValue("company_name", selectedOption.label);
  };

  const createCompany = async (companyName) => {
    const trimmedName = String(companyName || "").trim();

    if (!trimmedName) return null;

    const existing = companyOptions.find(
      (c) => c.label.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existing) {
      setCompany(existing.value);
      setStoredValue("id_company", existing.value);
      setStoredValue("company_name", existing.label);
      return existing;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: trimmedName }),
      });

      const data = await res.json();

      if (!res.ok || !data?.id_company) return null;

      const newCompany = {
        label: data.company_name,
        value: data.id_company,
      };

      setCompanyOptions((prev) => [...prev, newCompany]);
      setCompany(newCompany.value);
      setStoredValue("id_company", newCompany.value);
      setStoredValue("company_name", newCompany.label);

      return newCompany;
    } catch (error) {
      console.error("Error al crear empresa:", error);
      return null;
    }
  };

  const handleInvitationCodeChange = (e) => {
    const upperCode = e.target.value.toUpperCase().trim();
    setInvitationCode(upperCode);
    setStoredValue("invitation_code", upperCode);
  };

  useEffect(() => {
    if (IS_FREE) {
      setCodeValid(false);
      setFinalPrice(0);
      sessionStorage.setItem("code_is_valid", "false");
      sessionStorage.setItem("final_price", "0");
      sessionStorage.setItem("currency", currency);
      return;
    }

    let cancelled = false;

    const run = async () => {
      const code = (invitationCode || "").trim();

      setInvitationError("");
      setInvitationSuccess("");

      if (!code) {
        if (!cancelled) {
          setCodeValid(false);
          setFinalPrice(BASE_PRICE);
          sessionStorage.setItem("code_is_valid", "false");
          sessionStorage.setItem("final_price", String(BASE_PRICE));
          sessionStorage.setItem("currency", currency);
        }
        return;
      }

      if (!CODE_RE) {
        if (!cancelled) {
          setCodeValid(false);
          setFinalPrice(BASE_PRICE);
          setInvitationError(
            publicConfigError || t[language].configError
          );
        }
        return;
      }

      if (!CODE_RE.test(code)) {
        if (!cancelled) {
          setCodeValid(false);
          setFinalPrice(BASE_PRICE);
          setInvitationError(
            publicConfig?.invite_code_hint
              ? `${t[language].invalidFormatHint} ${publicConfig.invite_code_hint}`
              : t[language].invalidFormat
          );
        }
        return;
      }

      setIsValidatingCode(true);

      try {
        const res = await fetch(`${BACKEND_URL}/validate-code/${encodeURIComponent(code)}`);
        const data = await res.json();

        if (cancelled) return;

        if (data?.valid) {
          setCodeValid(true);
          setFinalPrice(0);
          sessionStorage.setItem("code_is_valid", "true");
          sessionStorage.setItem("final_price", "0");
          sessionStorage.setItem("currency", currency);
          setInvitationError("");
          setInvitationSuccess(t[language].freeEntry);
        } else {
          setCodeValid(false);
          setFinalPrice(BASE_PRICE);
          sessionStorage.setItem("code_is_valid", "false");
          sessionStorage.setItem("final_price", String(BASE_PRICE));
          sessionStorage.setItem("currency", currency);
          setInvitationSuccess("");

          const reason = data?.reason || "unknown";
          if (reason === "limit_reached") {
            setInvitationError(t[language].usageLimit);
          } else if (reason === "not_found") {
            setInvitationError(t[language].codeNotFound);
          } else if (reason === "invalid_format") {
            setInvitationError(t[language].invalidFormat);
          } else {
            setInvitationError(t[language].verifyError);
          }
        }
      } catch {
        if (!cancelled) {
          setCodeValid(false);
          setFinalPrice(BASE_PRICE);
          setInvitationError(t[language].validatingError);
        }
      } finally {
        if (!cancelled) setIsValidatingCode(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [invitationCode, BASE_PRICE, CODE_RE, publicConfig, publicConfigError, currency]);

  const validateDni = useCallback(async (value) => {
    const cleanDni = String(value || "").replace(/\D/g, "");

    if (!cleanDni) {
      setDniStatusError("");
      return true;
    }

    if (!/^\d{6,10}$/.test(cleanDni)) {
      setDniStatusError("El DNI ingresado no tiene el formato correcto.");
      return false;
    }

    try {
      setIsValidatingDni(true);
      setDniStatusError("");

      const res = await fetch(
        `${BACKEND_URL}/validate-dni/${encodeURIComponent(cleanDni)}`
      );
      const data = await res.json();

      if (!data.valid) {
        const reason = data.reason;

        if (reason === "approved") {
          setDniStatusError("Este DNI ya se registró. Revisá tu correo electrónico para ver el resumen.");
        } else if (reason === "pending") {
          setDniStatusError("Este DNI tiene un pago pendiente. Esperá a que se confirme para volver a intentar.");
        } else if (reason === "in_process") {
          setDniStatusError("Este DNI tiene un pago en proceso. Esperá a que se confirme para volver a intentar.");
        } else if (reason === "invited") {
          setDniStatusError("Este DNI ya tiene un código de invitación cargado. No puede volver a registrarse.");
        } else if (reason === "email_in_use_by_other_dni") {
          setDniStatusError("Este DNI ya está asociado a otro email.");
        } else if (reason === "invalid_format") {
          setDniStatusError("El DNI ingresado no tiene el formato correcto.");
        } else if (reason === "missing_payment") {
          setDniStatusError("Este DNI no tiene datos de pago. Por favor, completá el proceso correctamente.");
        } else if (reason === "rejected") {
          setDniStatusError("");
          return true;
        } else {
          setDniStatusError("No se puede usar este DNI.");
        }
        return false;
      }

      setDniStatusError("");
      return true;
    } catch (err) {
      console.error("Error validando DNI:", err);
      setDniStatusError("Error validando el DNI. Intentalo nuevamente.");
      return false;
    } finally {
      setIsValidatingDni(false);
    }
  }, []);

  const parseStoredMultiValue = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [value];
    }
  };

  const isOtherValue = (value) => value === "Other" || value === "Otro" || value === "Other [Specify]" || value === "Otro [Especificar]";
  const mainInterestHasOther = (value) =>
    parseStoredMultiValue(value).some((item) => isOtherValue(item));

  const handleDniBlur = () => {
    if (USA_MODE) return;
    validateDni(dni);
  };

  const btnNext = async () => {
    const errors = {};

    if (USA_MODE) {
      if (!fullName.trim()) errors.fullName = "Full name is required";
    } else {
      if (!firstName.trim()) errors.firstName = "El nombre es obligatorio";
      if (!lastName.trim()) errors.lastName = "El apellido es obligatorio";
    }

    if (!USA_MODE && !dni.trim()) errors.dni = "El DNI es obligatorio";
    if (!phone.trim()) {
      errors.phone = USA_MODE
        ? "Phone number is required"
        : "El teléfono es obligatorio";
    }
    if (!company) {
      errors.company = USA_MODE
        ? "Company is required"
        : "La compañía es obligatoria";
    }
    if (!validarEmail(email)) {
      errors.email = USA_MODE ? "Invalid email address." : "Email inválido.";
    }
    if (!position.trim()) {
      errors.position = USA_MODE
        ? "Job title is required"
        : "El puesto es obligatorio";
    }
    if (!region.trim()) {
      errors.region = USA_MODE
        ? "State is required"
        : "La provincia es obligatoria";
    }
    if (!sector.trim()) {
      errors.sector = USA_MODE
        ? "Industry is required"
        : "El rubro es obligatorio";
    }
    if (isOtherValue(mainRole) && !String(mainRoleOther || "").trim()) {
      errors.mainRoleOther = USA_MODE
        ? "Please specify your main role"
        : "Especificá tu rol principal";
    }
    if (mainInterestHasOther(mainInterest) && !String(mainInterestOther || "").trim()) {
      errors.mainInterestOther = USA_MODE
        ? "Please specify your main interest"
        : "Especificá qué te interesa principalmente del evento";
    }
    if (isOtherValue(eventSource) && !String(eventSourceOther || "").trim()) {
      errors.eventSourceOther = USA_MODE
        ? "Please specify how you heard about the event"
        : "Especificá cómo te enteraste del evento";
    }

    if (!IS_FREE && invitationCode && !codeValid) {
      setInvitationError(t[language].codeNotValid);
      return;
    }

    if (Object.keys(errors).length === 0 && !USA_MODE) {
      const isDniValid = await validateDni(dni);
      if (!isDniValid) return;
    }

    if (dniStatusError || emailError) {
      setFormErrors((prev) => ({
        ...prev,
        ...(dniStatusError ? { dni: dniStatusError } : {}),
        ...(emailError ? { email: emailError } : {}),
      }));
      return;
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setInvitationError("");

    const normalizedFullName = USA_MODE
      ? fullName.trim()
      : `${firstName} ${lastName}`.trim();

    sessionStorage.setItem("full_name", normalizedFullName);

    const parts = normalizedFullName.split(/\s+/).filter(Boolean);
    const fn = parts[0] || "";
    const ln = parts.slice(1).join(" ") || "";

    sessionStorage.setItem("first_name", fn);
    sessionStorage.setItem("last_name", ln);

    const payload = {
      first_name: USA_MODE ? fn : firstName.trim(),
      last_name: USA_MODE ? ln : lastName.trim(),
      dni: USA_MODE ? "N/A" : dni.trim(),
      email: email.trim(),
      phone: phone.trim(),
      position: position.trim(),
      region: region.trim(),
      sector: sector.trim(),
      employees,
      id_company: company || null,
      company_name: sessionStorage.getItem("company_name") || "",
      panel_topics: (panelTopics ?? sessionStorage.getItem("panel_topics") ?? "").trim(),
      extra_answers: {},
      air_travel: "0",
      travel_time: 0,
      event_transport: "N/A",
      km_land: 0,
      stayed_in_hotel: 0,
      hotel_nights: 0,
      invitation_code: !IS_FREE && codeValid ? (invitationCode || "").trim() : "",
      inscripto: false,
      business_group: businessGroup,
      main_role: mainRole,
      main_role_other: mainRoleOther,
      main_interest: mainInterest,
      main_interest_other: mainInterestOther,
      event_source: eventSource,
      event_source_other: eventSourceOther,
    };

    try {
      const res = await fetch(`${BACKEND_URL}/responses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.status === "error") {
        const code = data?.code || "";

        if (code === "email_in_use_by_other_dni") {
          setEmailError("Este email ya está asociado a otro asistente.");
          return;
        }
        if (code === "approved") {
          setEmailError("Este email ya tiene una inscripción aprobada.");
          return;
        }
        if (code === "pending" || code === "in_process") {
          setEmailError("Este email tiene un pago en proceso. Esperá e intentá nuevamente.");
          return;
        }
        if (code === "invited_exists") {
          setEmailError("Este email ya fue confirmado con un código de invitación.");
          return;
        }

        showPopup(
          data?.error || "No pudimos guardar tu inscripción. Intentá nuevamente en unos minutos.",
          {
            title: "No pudimos guardar tu inscripción",
            type: "error",
          }
        );
        return;
      }

      const userId = data?.user_id;
      if (!userId) {
        showPopup("No recibimos tu ID de inscripción. Intentá nuevamente.", {
          title: "No pudimos completar la inscripción",
          type: "error",
        });
        return;
      }

      sessionStorage.setItem("user_id", String(userId));
      localStorage.setItem("user_id", String(userId));
      sessionStorage.setItem("final_price", String(finalPrice));
      sessionStorage.setItem("currency", currency);
      localStorage.setItem("code_is_valid", sessionStorage.getItem("code_is_valid") || "false");
      localStorage.setItem("email", email.trim());
      localStorage.setItem("final_price", String(finalPrice));
      localStorage.setItem("currency", currency);
    } catch (e) {
      console.error("POST /responses error:", e);
      showPopup("Hubo un problema de conexión. Intentá nuevamente.", {
        title: "No pudimos guardar tu inscripción",
        type: "error",
      });
      return;
    }

    if (ENABLE_EMISSION) return navigate("/emission");
    if (ENABLE_QUESTIONS) return navigate("/questions");

    const codeIsValid = sessionStorage.getItem("code_is_valid") === "true";
    const shouldPay = !IS_FREE && !codeIsValid;

    if (shouldPay) navigate("/summary");
    else navigate("/thanks");
  };

const paisesRegiones = [
  "Argentina - Buenos Aires",
  "Argentina - Catamarca",
  "Argentina - Chaco",
  "Argentina - Chubut",
  "Argentina - Ciudad Autónoma de Buenos Aires",
  "Argentina - Córdoba",
  "Argentina - Corrientes",
  "Argentina - Entre Ríos",
  "Argentina - Formosa",
  "Argentina - Jujuy",
  "Argentina - La Pampa",
  "Argentina - La Rioja",
  "Argentina - Mendoza",
  "Argentina - Misiones",
  "Argentina - Neuquén",
  "Argentina - Río Negro",
  "Argentina - Salta",
  "Argentina - San Juan",
  "Argentina - San Luis",
  "Argentina - Santa Cruz",
  "Argentina - Santa Fe",
  "Argentina - Santiago del Estero",
  "Argentina - Tierra del Fuego",
  "Argentina - Tucumán",

  "Bolivia - Beni",
  "Bolivia - Chuquisaca",
  "Bolivia - Cochabamba",
  "Bolivia - La Paz",
  "Bolivia - Oruro",
  "Bolivia - Pando",
  "Bolivia - Potosí",
  "Bolivia - Santa Cruz",
  "Bolivia - Tarija",

  "Brasil - Acre",
  "Brasil - Alagoas",
  "Brasil - Amapá",
  "Brasil - Amazonas",
  "Brasil - Bahía",
  "Brasil - Ceará",
  "Brasil - Distrito Federal",
  "Brasil - Espírito Santo",
  "Brasil - Goiás",
  "Brasil - Maranhão",
  "Brasil - Mato Grosso",
  "Brasil - Mato Grosso do Sul",
  "Brasil - Minas Gerais",
  "Brasil - Pará",
  "Brasil - Paraíba",
  "Brasil - Paraná",
  "Brasil - Pernambuco",
  "Brasil - Piauí",
  "Brasil - Río de Janeiro",
  "Brasil - Río Grande do Norte",
  "Brasil - Río Grande do Sul",
  "Brasil - Rondônia",
  "Brasil - Roraima",
  "Brasil - Santa Catarina",
  "Brasil - São Paulo",
  "Brasil - Sergipe",
  "Brasil - Tocantins",

  "Chile - Aisén",
  "Chile - Antofagasta",
  "Chile - Arica y Parinacota",
  "Chile - Atacama",
  "Chile - Biobío",
  "Chile - Coquimbo",
  "Chile - La Araucanía",
  "Chile - Los Lagos",
  "Chile - Los Ríos",
  "Chile - Magallanes",
  "Chile - Maule",
  "Chile - Ñuble",
  "Chile - O’Higgins",
  "Chile - Región Metropolitana de Santiago",
  "Chile - Tarapacá",
  "Chile - Valparaíso",

  "Colombia - Amazonas",
  "Colombia - Antioquia",
  "Colombia - Arauca",
  "Colombia - Atlántico",
  "Colombia - Bolívar",
  "Colombia - Boyacá",
  "Colombia - Caldas",
  "Colombia - Caquetá",
  "Colombia - Casanare",
  "Colombia - Cauca",
  "Colombia - Cesar",
  "Colombia - Chocó",
  "Colombia - Córdoba",
  "Colombia - Cundinamarca",
  "Colombia - Guainía",
  "Colombia - Guaviare",
  "Colombia - Huila",
  "Colombia - La Guajira",
  "Colombia - Magdalena",
  "Colombia - Meta",
  "Colombia - Nariño",
  "Colombia - Norte de Santander",
  "Colombia - Putumayo",
  "Colombia - Quindío",
  "Colombia - Risaralda",
  "Colombia - San Andrés y Providencia",
  "Colombia - Santander",
  "Colombia - Sucre",
  "Colombia - Tolima",
  "Colombia - Valle del Cauca",
  "Colombia - Vaupés",
  "Colombia - Vichada",

  "Estados Unidos - Alabama",
  "Estados Unidos - Alaska",
  "Estados Unidos - Arizona",
  "Estados Unidos - Arkansas",
  "Estados Unidos - California",
  "Estados Unidos - Carolina del Norte",
  "Estados Unidos - Carolina del Sur",
  "Estados Unidos - Colorado",
  "Estados Unidos - Connecticut",
  "Estados Unidos - Dakota del Norte",
  "Estados Unidos - Dakota del Sur",
  "Estados Unidos - Delaware",
  "Estados Unidos - Florida",
  "Estados Unidos - Georgia",
  "Estados Unidos - Hawái",
  "Estados Unidos - Idaho",
  "Estados Unidos - Illinois",
  "Estados Unidos - Indiana",
  "Estados Unidos - Iowa",
  "Estados Unidos - Kansas",
  "Estados Unidos - Kentucky",
  "Estados Unidos - Luisiana",
  "Estados Unidos - Maine",
  "Estados Unidos - Maryland",
  "Estados Unidos - Massachusetts",
  "Estados Unidos - Michigan",
  "Estados Unidos - Minnesota",
  "Estados Unidos - Misisipi",
  "Estados Unidos - Misuri",
  "Estados Unidos - Montana",
  "Estados Unidos - Nebraska",
  "Estados Unidos - Nevada",
  "Estados Unidos - Nueva Hampshire",
  "Estados Unidos - Nueva Jersey",
  "Estados Unidos - Nueva York",
  "Estados Unidos - Nuevo México",
  "Estados Unidos - Ohio",
  "Estados Unidos - Oklahoma",
  "Estados Unidos - Oregón",
  "Estados Unidos - Pensilvania",
  "Estados Unidos - Rhode Island",
  "Estados Unidos - Tennessee",
  "Estados Unidos - Texas",
  "Estados Unidos - Utah",
  "Estados Unidos - Vermont",
  "Estados Unidos - Virginia",
  "Estados Unidos - Virginia Occidental",
  "Estados Unidos - Washington",
  "Estados Unidos - Wisconsin",
  "Estados Unidos - Wyoming",

  "México - Aguascalientes",
  "México - Baja California",
  "México - Baja California Sur",
  "México - Campeche",
  "México - Chiapas",
  "México - Chihuahua",
  "México - Ciudad de México",
  "México - Coahuila",
  "México - Colima",
  "México - Durango",
  "México - Estado de México",
  "México - Guanajuato",
  "México - Guerrero",
  "México - Hidalgo",
  "México - Jalisco",
  "México - Michoacán",
  "México - Morelos",
  "México - Nayarit",
  "México - Nuevo León",
  "México - Oaxaca",
  "México - Puebla",
  "México - Querétaro",
  "México - Quintana Roo",
  "México - San Luis Potosí",
  "México - Sinaloa",
  "México - Sonora",
  "México - Tabasco",
  "México - Tamaulipas",
  "México - Tlaxcala",
  "México - Veracruz",
  "México - Yucatán",
  "México - Zacatecas",

  "Uruguay - Artigas",
  "Uruguay - Canelones",
  "Uruguay - Cerro Largo",
  "Uruguay - Colonia",
  "Uruguay - Durazno",
  "Uruguay - Flores",
  "Uruguay - Florida",
  "Uruguay - Lavalleja",
  "Uruguay - Maldonado",
  "Uruguay - Montevideo",
  "Uruguay - Paysandú",
  "Uruguay - Río Negro",
  "Uruguay - Rivera",
  "Uruguay - Rocha",
  "Uruguay - Salto",
  "Uruguay - San José",
  "Uruguay - Soriano",
  "Uruguay - Tacuarembó",
  "Uruguay - Treinta y Tres"
];

  const sectorCompany = {
    EN: [
      "Operators",
      "Oilfield Services (drilling, completion, well services, etc.)",
      "Midstream",
      "Downstream",
      "Equipment and Manufacturing",
      "Engineering and Construction",
      "Energy Transition and Sustainability",
      "Finance and Investors",
      "Consulting, Legal and Advisory",
      "Electric power generation, transmission and distribution",
      "Public Sector",
      "Tecnology / Innovation"
    ],
    ES: [
      "Operadoras",
      "Servicios petroleros (perforación, completación, servicios de pozo, etc.)",
      "Midstream",
      "Downstream",
      "Equipamiento y manufactura",
      "Ingeniería y construcción",
      "Transición energética y sustentabilidad",
      "Finanzas e inversores",
      "Consultoría, legales y asesoramiento",
      "Generación, transporte y distribución eléctrica",
      "Sector público y académico",
      "Tecnología / Innovación",
    ],
  };

  return {
    USA_MODE,
    fullName,
    setFullName,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dni,
    setDni,
    handleDniBlur,
    email,
    setEmail,
    phone,
    setPhone,
    position,
    setPosition,
    region,
    setRegion,
    sector,
    setSector,
    employees,
    setEmployees,
    invitationCode,
    setInvitationCode,
    formErrors,
    invitationError,
    invitationSuccess,
    dniStatusError,
    isValidatingDni,
    isValidatingEmail,
    isValidatingCode,
    panelTopics,
    setPanelTopics,
    publicConfig,
    publicConfigError,
    isLoadingPublicConfig,
    currency,
    finalPrice,
    company,
    companyOptions,
    paisesRegiones,
    sectorCompany,
    setStoredValue,
    handleCompanyChange,
    createCompany,
    handleInvitationCodeChange,
    btnNext,
    IS_FREE,
    businessGroup,
    setBusinessGroup,
    mainRole,
    setMainRole,
    mainRoleOther,
    setMainRoleOther,
    mainInterest,
    setMainInterest,
    mainInterestOther,
    setMainInterestOther,
    eventSource,
    setEventSource,
    eventSourceOther,
    setEventSourceOther,
  };
}

